import React, { useState, useEffect } from 'react';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { db } from '../../firebase';
import {
  Container,
  Paper,
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Alert
} from '@mui/material';

export default function QRCodeDebug() {
  const [tournaments, setTournaments] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchTournaments();
  }, []);

  const fetchTournaments = async () => {
    try {
      setLoading(true);
      const tournamentsSnapshot = await getDocs(collection(db, 'tournaments'));
      const tournamentsData = [];
      
      for (const doc of tournamentsSnapshot.docs) {
        const tournamentData = { id: doc.id, ...doc.data() };
        
        // Fetch players for this tournament
        try {
          const playersQuery = query(
            collection(db, 'players'),
            where('organizationId', '==', tournamentData.organizationId)
          );
          const playersSnapshot = await getDocs(playersQuery);
          tournamentData.playerCount = playersSnapshot.docs.length;
        } catch (error) {
          console.error('Error fetching players for tournament:', tournamentData.id, error);
          tournamentData.playerCount = 0;
        }
        
        tournamentsData.push(tournamentData);
      }
      
      setTournaments(tournamentsData);
    } catch (error) {
      console.error('Error fetching tournaments:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Container sx={{ mt: 4 }}>
        <Typography>Loading tournaments...</Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Typography variant="h4" gutterBottom>
        QR Code Debug - All Tournaments
      </Typography>
      
      <Alert severity="info" sx={{ mb: 3 }}>
        This shows all tournaments and their QR codes for debugging purposes.
      </Alert>

      {tournaments.map(tournament => (
        <Paper key={tournament.id} sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            {tournament.name} (ID: {tournament.id})
          </Typography>
          
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Players: {tournament.playerCount || 0}
          </Typography>
          
          <Typography variant="body2" color="text.secondary" gutterBottom>
            QR Codes: {Object.keys(tournament.qrCodes || {}).length}
          </Typography>

          {Object.keys(tournament.qrCodes || {}).length > 0 ? (
            <TableContainer sx={{ mt: 2 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Player ID</TableCell>
                    <TableCell>QR Code</TableCell>
                    <TableCell>Generated At</TableCell>
                    <TableCell>Expires At</TableCell>
                    <TableCell>Active</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {Object.entries(tournament.qrCodes).map(([playerId, qrData]) => (
                    <TableRow key={playerId}>
                      <TableCell>{playerId}</TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                          {qrData.code}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {qrData.generatedAt?.toDate ? 
                          qrData.generatedAt.toDate().toLocaleString() : 
                          new Date(qrData.generatedAt).toLocaleString()
                        }
                      </TableCell>
                      <TableCell>
                        {qrData.expiresAt?.toDate ? 
                          qrData.expiresAt.toDate().toLocaleString() : 
                          new Date(qrData.expiresAt).toLocaleString()
                        }
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={qrData.isActive ? 'Active' : 'Inactive'} 
                          color={qrData.isActive ? 'success' : 'error'}
                          size="small"
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Alert severity="warning" sx={{ mt: 2 }}>
              No QR codes found for this tournament.
            </Alert>
          )}
        </Paper>
      ))}

      {tournaments.length === 0 && (
        <Alert severity="warning">
          No tournaments found in the database.
        </Alert>
      )}
    </Container>
  );
}
